import React from 'react';
import { Link } from 'react-router-dom';
import { Car, Home, Building2, Shield, Star, CheckCircle, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { services } from '../data/mock';
import PhotoGallery from '../components/PhotoGallery';

const SERVICE_ICONS = {
  automotive: Car,
  residential: Home,
  commercial: Building2,
};

const ServicesPage = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-gray-50 to-gray-100 py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-4 sm:space-y-6">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900">
              Professional Window Tinting <span className="text-red-600">Services</span>
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              We specialize in automotive, residential, and commercial window tinting using premium films
              and expert installation techniques for lasting quality and performance.
            </p>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-12 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {services.map((service) => {
              const Icon = SERVICE_ICONS[service.id] || Shield;
              return (
                <Card key={service.id} className="border-0 shadow-xl hover:shadow-2xl transition-all duration-300">
                  <CardHeader className="text-center pb-6">
                    <div className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 bg-red-100 rounded-full mb-4 sm:mb-6">
                      <Icon className="h-7 w-7 sm:h-8 sm:w-8 text-red-600" />
                    </div>
                    <CardTitle className="text-xl sm:text-2xl font-bold text-gray-900 mb-3">
                      {service.title}
                    </CardTitle>
                    <p className="text-gray-600 leading-relaxed text-sm sm:text-base">
                      {service.description}
                    </p>
                  </CardHeader>
                  <CardContent className="space-y-4 sm:space-y-6">
                    <div className="space-y-2 sm:space-y-3">
                      <h4 className="font-semibold text-gray-900 text-sm sm:text-base">Key Benefits:</h4>
                      {service.features.map((feature, index) => (
                        <div key={index} className="flex items-center space-x-2 sm:space-x-3">
                          <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-green-600 flex-shrink-0" />
                          <span className="text-gray-700 text-sm">{feature}</span>
                        </div>
                      ))}
                    </div>
                    <div className="pt-2 sm:pt-4">
                      <Button asChild className="bg-red-600 hover:bg-red-700 text-white w-full py-3 rounded-lg transition-all duration-200 hover:shadow-lg">
                        <Link to="/book">Get {service.id.charAt(0).toUpperCase() + service.id.slice(1)} Quote</Link>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Automotive Film Options */}
      <section className="py-12 sm:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Automotive Film Options
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto">
              Choose from our premium selection of automotive window films.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            {services[0].filmOptions.map((film, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <CardHeader>
                  <CardTitle className="text-lg sm:text-xl font-bold text-gray-900 text-center">{film.name}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 sm:space-y-6">
                  <p className="text-gray-600 text-center leading-relaxed text-sm sm:text-base">{film.description}</p>
                  <div className="space-y-3">
                    <h4 className="font-semibold text-gray-900 text-center text-sm sm:text-base">Available Tint Levels:</h4>
                    <div className="flex flex-wrap justify-center gap-2">
                      {film.tintLevels.map((level, i) => (
                        <span key={i} className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-medium">{level}</span>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Residential Film Options */}
      <section className="py-12 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Residential Film Solutions
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto">
              Enhance your home's comfort, privacy, and energy efficiency.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            {services[1].filmOptions.map((film, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <CardHeader>
                  <CardTitle className="text-lg sm:text-xl font-bold text-gray-900 text-center">{film.name}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 sm:space-y-6">
                  <p className="text-gray-600 text-center leading-relaxed text-sm sm:text-base">{film.description}</p>
                  <div className="space-y-2 sm:space-y-3">
                    <h4 className="font-semibold text-gray-900 text-sm sm:text-base">Key Benefits:</h4>
                    {film.benefits.map((benefit, i) => (
                      <div key={i} className="flex items-center space-x-2 sm:space-x-3">
                        <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                        <span className="text-gray-700 text-sm">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Commercial Film Options */}
      <section className="py-12 sm:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Commercial Film Solutions
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto">
              Professional-grade films for offices, storefronts, and commercial buildings.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            {services[2].filmOptions.map((film, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <CardHeader>
                  <CardTitle className="text-lg sm:text-xl font-bold text-gray-900 text-center">{film.name}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 sm:space-y-6">
                  <p className="text-gray-600 text-center leading-relaxed text-sm sm:text-base">{film.description}</p>
                  <div className="space-y-2 sm:space-y-3">
                    <h4 className="font-semibold text-gray-900 text-sm sm:text-base">Key Benefits:</h4>
                    {film.benefits.map((benefit, i) => (
                      <div key={i} className="flex items-center space-x-2 sm:space-x-3">
                        <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                        <span className="text-gray-700 text-sm">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-12 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose Our Services?
            </h2>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {[
              { icon: Shield, title: 'Premium Quality', desc: 'Top-grade films from trusted manufacturers with proven performance.' },
              { icon: Star, title: 'Expert Installation', desc: 'Skilled technicians with years of experience and attention to detail.' },
              { icon: CheckCircle, title: 'Warranty Protection', desc: 'Comprehensive warranty coverage for materials and workmanship.' },
              { icon: ArrowRight, title: 'Fast Service', desc: 'Quick turnaround times without compromising on quality.' },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className="text-center space-y-3 sm:space-y-4">
                  <div className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 bg-red-100 rounded-full">
                    <Icon className="h-6 w-6 sm:h-8 sm:w-8 text-red-600" />
                  </div>
                  <h3 className="text-base sm:text-xl font-semibold text-gray-900">{item.title}</h3>
                  <p className="text-gray-600 text-sm hidden sm:block">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Photo Gallery */}
      <PhotoGallery />

      {/* CTA Section */}
      <section className="py-16 sm:py-20 bg-red-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4 sm:mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-lg sm:text-xl text-red-100 mb-6 sm:mb-8 leading-relaxed">
            Contact us today for a free consultation and estimate. We'll help you choose the perfect window tinting solution.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-white text-red-600 hover:bg-gray-100 px-8 py-4 text-lg rounded-lg transition-all duration-200 hover:shadow-lg">
              <Link to="/book">Book Appointment</Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="border-2 border-white text-white hover:bg-white hover:text-red-600 px-8 py-4 text-lg rounded-lg transition-all duration-200">
              <a href="tel:(713)446-8928">Call (713) 446-8928</a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
